/**
 * calibrationService.ts
 *
 * Step 4 — Calibration. Implements CLAUDE.md §E:
 *   - One CalibrationSession per cycle
 *   - Manager prep (§E.2): 2-3 sentence justification per direct report
 *   - Distribution check against bell-curve targets (§E.4) with
 *     within/review/out status (§E.5)
 *   - Immutable adjustment audit (§E.6)
 *   - Lock blocks when any band is 'out'; rewrites appraisal eprSummary
 *
 * Score-to-band mapping. The framework.docx implies the thresholds below
 * via the sample calibration table (4.8→Exceptional, 3.7→Exceeds, 3.3→
 * Meets). HR may override per cycle by passing a custom bandThresholds
 * to lockSession; otherwise SCORE_TO_BAND_DEFAULT applies.
 */

import {
  doc, getDoc, setDoc, updateDoc, addDoc, collection, getDocs,
  query, where, serverTimestamp, runTransaction, Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type {
  CalibrationSession, CalibrationBand, BandDistribution, BandRow,
  CalibrationAdjustment, ManagerPrep, CalibrationAttendees, CalibrationSessionStatus,
  BandTargets,
} from '../types/calibration';
import {
  BELL_CURVE_TARGETS, CALIBRATION_BANDS,
} from '../types/calibration';
import type { EPRSubmission } from '../types/eprFramework';
import { listSubmissionsForCycle } from './eprSubmissionService';
import { consolidateOne, buildEPRSummary, appraisalId } from './consolidationService';
import { omitUndefined } from './firestoreUtils';

const SESSION_COL    = 'calibrationSessions';
const ADJUSTMENT_COL = 'calibrationAdjustments';

// ─── Score → band mapping ──────────────────────────────────────────

export interface BandThresholds {
  exceptionalMin:       number;   // inclusive
  exceedMin:            number;
  meetMin:              number;
  needsImprovementMin:  number;
}

export const SCORE_TO_BAND_DEFAULT: BandThresholds = {
  exceptionalMin:      4.50,
  exceedMin:           3.75,
  meetMin:             3.00,
  needsImprovementMin: 2.00,
};

export function scoreToBand(
  score: number,
  thresholds: BandThresholds = SCORE_TO_BAND_DEFAULT,
): CalibrationBand {
  if (score >= thresholds.exceptionalMin) return 'exceptional';
  if (score >= thresholds.exceedMin)      return 'exceed';
  if (score >= thresholds.meetMin)        return 'meet';
  if (score >= thresholds.needsImprovementMin) return 'needs_improvement';
  return 'unsatisfactory';
}

// ─── Errors ────────────────────────────────────────────────────────

export class CalibrationError extends Error {
  constructor(
    public code:
      | 'not_found'
      | 'wrong_state'
      | 'out_of_range'
      | 'missing_justification',
    message: string,
  ) {
    super(message);
    this.name = 'CalibrationError';
  }
}

// ─── Session lifecycle ─────────────────────────────────────────────

export interface CreateSessionInput {
  cycleId: string;
  attendees: CalibrationAttendees;
}

export async function createSession(input: CreateSessionInput): Promise<CalibrationSession> {
  const ref = doc(db, SESSION_COL, input.cycleId);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    return { id: snap.id, ...(snap.data() as Omit<CalibrationSession, 'id'>) };
  }
  const subs = await listSubmissionsForCycle(input.cycleId);
  const totalEmployees = subs.filter(
    s => s.status === 'manager_submitted' || s.status === 'locked',
  ).length;

  const initial: BandDistribution = emptyDistribution();

  const data: Omit<CalibrationSession, 'id'> = {
    cycleId:           input.cycleId,
    status:            'preparing' as CalibrationSessionStatus,
    attendees:         input.attendees,
    managerPreps:      [],
    distribution:      initial,
    totalEmployees,
    createdAt:         serverTimestamp() as unknown as Timestamp,
  };
  await setDoc(ref, data);
  const fresh = await getDoc(ref);
  return { id: input.cycleId, ...(fresh.data() as Omit<CalibrationSession, 'id'>) };
}

function emptyDistribution(): BandDistribution {
  const acc = {} as Record<CalibrationBand, BandRow>;
  for (const b of CALIBRATION_BANDS) acc[b] = { count: 0, pct: 0, status: 'within' };
  return acc as BandDistribution;
}

// ─── Manager prep (§E.2) ───────────────────────────────────────────

export async function recordManagerPrep(
  cycleId: string,
  prep: ManagerPrep,
): Promise<void> {
  const ref = doc(db, SESSION_COL, cycleId);
  await runTransaction(db, async tx => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      throw new CalibrationError('not_found', 'Calibration session not found.');
    }
    const sess = snap.data() as CalibrationSession;
    if (sess.status === 'locked') {
      throw new CalibrationError('wrong_state', 'Calibration is locked.');
    }
    if (!prep.justification.trim() || prep.justification.trim().length < 10) {
      throw new CalibrationError(
        'missing_justification',
        'Justifikasi minimal 2-3 kalimat (≥ 10 karakter).',
      );
    }
    const existing = sess.managerPreps.filter(
      p => !(p.employeeId === prep.employeeId && p.managerId === prep.managerId),
    );
    tx.update(ref, {
      managerPreps: [...existing, prep],
      updatedAt: serverTimestamp(),
    });
  });
}

// ─── Distribution compute ──────────────────────────────────────────

export interface DistributionInput {
  cycleId: string;
  /** Optional override; defaults to SCORE_TO_BAND_DEFAULT. */
  thresholds?: BandThresholds;
  /**
   * Optional justification text per band, applied to rows that end up
   * with status='review'. Each band may also be edited later via
   * updateDistributionJustification.
   */
  justifications?: Partial<Record<CalibrationBand, string>>;
}

/**
 * Compute distribution from the current EPR scores (calibratedTotal ??
 * rawTotal). Writes the resulting distribution onto the session doc.
 */
export async function computeAndStoreDistribution(
  input: DistributionInput,
): Promise<CalibrationSession> {
  const subs = await listSubmissionsForCycle(input.cycleId);
  const ready = subs.filter(
    s => s.status === 'manager_submitted' || s.status === 'locked',
  );
  const thresholds = input.thresholds ?? SCORE_TO_BAND_DEFAULT;

  const counts: Record<CalibrationBand, number> = {
    exceptional: 0, exceed: 0, meet: 0, needs_improvement: 0, unsatisfactory: 0,
  };
  for (const s of ready) {
    const finalScore = s.scores.calibratedTotal ?? s.scores.rawTotal;
    if (typeof finalScore !== 'number') continue;
    counts[scoreToBand(finalScore, thresholds)] += 1;
  }
  const total = ready.length || 1;
  const distribution = {} as BandDistribution;
  for (const b of CALIBRATION_BANDS) {
    const pct = (counts[b] / total) * 100;
    const status = lockStatusFor(b, pct);
    distribution[b] = {
      count: counts[b],
      pct,
      status,
      justification: status === 'review' ? input.justifications?.[b] : undefined,
    };
  }

  const ref = doc(db, SESSION_COL, input.cycleId);
  await updateDoc(ref, {
    distribution,
    totalEmployees: ready.length,
    status: 'in_session',
    updatedAt: serverTimestamp(),
  });
  const fresh = await getDoc(ref);
  return { id: input.cycleId, ...(fresh.data() as Omit<CalibrationSession, 'id'>) };
}

export function lockStatusFor(band: CalibrationBand, pct: number): BandRow['status'] {
  const t: BandTargets = BELL_CURVE_TARGETS[band];
  if (pct >= t.target.min && pct <= t.target.max) return 'within';
  if (pct >= t.allowed.min && pct <= t.allowed.max) return 'review';
  return 'out';
}

export async function updateBandJustification(
  cycleId: string,
  band: CalibrationBand,
  justification: string,
): Promise<void> {
  const ref = doc(db, SESSION_COL, cycleId);
  await runTransaction(db, async tx => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new CalibrationError('not_found', 'Session not found.');
    const sess = snap.data() as CalibrationSession;
    if (sess.status === 'locked') {
      throw new CalibrationError('wrong_state', 'Calibration is locked.');
    }
    const row = sess.distribution[band];
    if (row.status !== 'review') {
      throw new CalibrationError(
        'wrong_state',
        `Justification only required for bands in 'review' status (band=${band}, status=${row.status}).`,
      );
    }
    tx.update(ref, {
      [`distribution.${band}.justification`]: justification,
      updatedAt: serverTimestamp(),
    });
  });
}

// ─── Adjustment audit (§E.6) ───────────────────────────────────────

export interface ApplyAdjustmentInput {
  cycleId: string;
  employeeId: string;
  submissionId: string;
  scope: CalibrationAdjustment['scope'];
  componentOrItemId?: string;
  originalScore: number;
  adjustedScore: number;
  reason: string;
  approvedBy: string;
}

/**
 * Record an immutable audit row AND write the adjusted score onto the
 * corresponding EPRSubmission field (calibrationAdjustedScore for items;
 * scores.calibratedTotal for the whole submission). Original raw scores
 * are NEVER overwritten.
 */
export async function applyAdjustment(input: ApplyAdjustmentInput): Promise<CalibrationAdjustment> {
  if (!input.reason.trim()) {
    throw new CalibrationError('missing_justification', 'Reason is required for any adjustment.');
  }

  // Strip undefined optional fields before addDoc (componentOrItemId is
  // unset when scope='total'). ENGINEERING.md §4.
  const audit = omitUndefined({
    cycleId:           input.cycleId,
    employeeId:        input.employeeId,
    submissionId:      input.submissionId,
    scope:             input.scope,
    componentOrItemId: input.componentOrItemId,
    originalScore:     input.originalScore,
    adjustedScore:     input.adjustedScore,
    reason:            input.reason.trim(),
    approvedBy:        input.approvedBy,
    adjustedAt:        serverTimestamp() as unknown as Timestamp,
  }) as Omit<CalibrationAdjustment, 'id'>;
  const ref = await addDoc(collection(db, ADJUSTMENT_COL), audit);

  // Apply to submission doc
  const subRef = doc(db, 'eprSubmissions', input.submissionId);
  if (input.scope === 'total') {
    await updateDoc(subRef, {
      'scores.calibratedTotal': input.adjustedScore,
      updatedAt: serverTimestamp(),
    });
  } else if (input.scope === 'item' && input.componentOrItemId) {
    await runTransaction(db, async tx => {
      const subSnap = await tx.get(subRef);
      if (!subSnap.exists()) throw new CalibrationError('not_found', 'Submission not found.');
      const sub = subSnap.data() as EPRSubmission;
      const buckets: Array<keyof Pick<EPRSubmission, 'okrItems' | 'kpiItems' | 'coreItems' | 'leadershipItems'>> = [
        'okrItems', 'kpiItems', 'coreItems', 'leadershipItems',
      ];
      for (const b of buckets) {
        const idx = sub[b].findIndex(it => it.itemId === input.componentOrItemId);
        if (idx >= 0) {
          const next = [...sub[b]];
          next[idx] = {
            ...next[idx],
            calibrationAdjustedScore: input.adjustedScore,
            calibrationReason:        input.reason.trim(),
          };
          tx.update(subRef, { [b]: next, updatedAt: serverTimestamp() });
          break;
        }
      }
    });
  }
  // 'component' scope: store on calibratedTotal-equivalent per component is
  // a richer model; Phase-1 we resolve at lock time, no per-component live
  // write needed.

  return { ...audit, id: ref.id, adjustedAt: Timestamp.fromDate(new Date()) };
}

export async function listAdjustmentsForCycle(cycleId: string): Promise<CalibrationAdjustment[]> {
  const q = query(collection(db, ADJUSTMENT_COL), where('cycleId', '==', cycleId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<CalibrationAdjustment, 'id'>) }));
}

// ─── Lock (§E.5) ───────────────────────────────────────────────────

export interface LockSessionInput {
  cycleId: string;
  lockedBy: string;
}

/**
 * Lock the session. Blocks when any band is 'out'. On success:
 *   1. Lock all manager_submitted EPRSubmissions
 *   2. Rewrite appraisal eprSummary with calibrated scores (consolidateOne)
 *   3. Advance ReviewCycle.currentStep = 4
 */
export async function lockSession(input: LockSessionInput): Promise<void> {
  const ref = doc(db, SESSION_COL, input.cycleId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new CalibrationError('not_found', 'Session not found.');
  const sess = snap.data() as CalibrationSession;

  if (sess.status === 'locked') return;

  const outBands = CALIBRATION_BANDS.filter(b => sess.distribution[b].status === 'out');
  if (outBands.length > 0) {
    throw new CalibrationError(
      'out_of_range',
      `Cannot lock: bands out of allowed range — ${outBands.join(', ')}.`,
    );
  }
  // Bands in 'review' must have justification
  const missing = CALIBRATION_BANDS.filter(
    b => sess.distribution[b].status === 'review' && !sess.distribution[b].justification?.trim(),
  );
  if (missing.length > 0) {
    throw new CalibrationError(
      'missing_justification',
      `Bands in review need justification: ${missing.join(', ')}.`,
    );
  }

  // Lock all ready submissions
  const subs = await listSubmissionsForCycle(input.cycleId);
  for (const s of subs) {
    if (s.status === 'manager_submitted') {
      await updateDoc(doc(db, 'eprSubmissions', s.id), {
        status: 'locked',
        lockedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }
  }

  // Rewrite appraisal summaries with calibrated scores
  for (const s of subs) {
    if (s.status !== 'manager_submitted' && s.status !== 'locked') continue;
    await consolidateOne({
      cycleId: input.cycleId,
      employeeId: s.employeeId,
      supervisorId: s.supervisorId,
    });
  }
  // Mark Step 5 readiness — set currentStep on cycle
  await updateDoc(doc(db, 'reviewCycles', input.cycleId), {
    currentStep: 5,
    updatedAt: serverTimestamp(),
  });

  await updateDoc(ref, {
    status: 'locked' as CalibrationSessionStatus,
    lockedAt: serverTimestamp(),
    lockedBy: input.lockedBy,
    updatedAt: serverTimestamp(),
  });
}

// ─── Read ──────────────────────────────────────────────────────────

export async function getSession(cycleId: string): Promise<CalibrationSession | null> {
  const snap = await getDoc(doc(db, SESSION_COL, cycleId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<CalibrationSession, 'id'>) };
}

// Re-export builder for convenience
export { buildEPRSummary, appraisalId };
